import { Typography } from '@material-ui/core';
import { WarningRounded } from '@material-ui/icons';
import { Color, Column, Columns, Inline, Stack } from '@superdispatch/ui';
import { Box, TextBox } from '@superdispatch/ui-lab';
import { useMemo } from 'react';
import { BankErrorSimpleBanner } from 'shared/errors/SuperPayBankErrors';
import { useDocumentTitle } from 'shared/layout/DocumentTitle';
import { SuggestedByShipperTag } from 'shared/modules/load-suggestions/SuggestedByShipperTag';
import { PostingPrivateTag } from 'shared/modules/loadboard/PostingPrivateTag';
import { SuperPayNudgingBanner } from 'shared/modules/superpay/SuperPayNudgingBanner';
import { WebViewLinkedText } from 'shared/routing/WebViewLinks';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import { useFlag } from 'shared/settings/FeatureToggles';
import { BrokerFeeLine } from 'shared/ui/BrokerFeeLine';
import { CarrierPostingStatusInfo } from '../../core/postings/CarrierPostingStatusInfo';
import { PostingLoadDetailsDates } from '../../core/postings/PostingLoadDetailsDates';
import { PostingEditedLabel } from '../../core/postings/PostingEditedLabel';
import { renderPostingOfferButton } from '../../core/postings/PostingOfferButton';
import { PostingPostedDate } from '../../core/postings/PostingPostedDate';
import { PostingRouteInfo } from '../../core/postings/PostingRouteInfo';
import { PostingShipperDetails } from '../../core/postings/PostingShipperDetails';
import { PostingShipperRequirements } from '../../core/postings/PostingShipperRequirements';
import {
  PostingVehicles,
  PostingVehiclesExpander,
} from '../../core/postings/PostingVehicles';
import { SendRequestForm } from '../../core/postings/SendRequestForm';
import { UpdateRequestForm } from '../../core/postings/UpdateRequestForm';
import { SavedLoadStatus } from '../../core/saved-loads/SavedLoadStatus';
import { PostingDTO, getPostingState } from '../../data/dto/PostingDTO';
import {
  getShipperInBusinessSinceYear,
  hasShipperRequirements,
  shouldReviewShipperRequirements,
} from '../../data/dto/PostingLoadDTO';
import { PostingDistancePriceDetails } from '../PostingDistancePriceDetails';
import { PostingNumber } from '../PostingNumber';
import { PostingPaymentDetails } from '../PostingPaymentDetails';
import { PostingPriceLabel } from '../PostingPriceLabel';
import { PostingDetailsCard } from './PostingDetailsCard';
import { PostingDetailsInsuranceRequirements } from './PostingDetailsInsuranceRequirements';
import { PostingDetailsNearbyShipperLoads } from './PostingDetailsNearbyShipperLoads';

interface PostingViewProps {
  posting: PostingDTO;
  onScrollToFormClick: () => void;
}

export function PostingDetails({
  posting,
  onScrollToFormClick,
  posting: { load, carrier_load: carrierPostingInfo },
}: PostingViewProps) {
  const { data: settings } = useCarrierSettings();
  const isEditedByShipperFlagEnabled = useFlag('slb_edited_by_shipper');
  const postingStatus = getPostingState(load, carrierPostingInfo);

  const inBusinessSince = useMemo(
    () =>
      load.shipper?.in_business_since &&
      getShipperInBusinessSinceYear(load.shipper.in_business_since),
    [load.shipper],
  );

  useDocumentTitle(load.number);

  return (
    <Stack space="xsmall">
      <PostingDetailsCard>
        <Stack space="xxsmall">
          {posting.is_shipper_recommended && <SuggestedByShipperTag />}

          <Columns>
            <Column>
              <Stack space="xsmall">
                {load.is_posted_to_private_loadboard && (
                  <Inline>
                    <PostingPrivateTag />
                  </Inline>
                )}

                <Inline space="xsmall" verticalAlign="bottom">
                  <PostingPriceLabel
                    load={load}
                    isBookable={postingStatus === 'can_book'}
                  />

                  <PostingDistancePriceDetails load={load} />
                </Inline>
              </Stack>
            </Column>

            <Column width="content">
              <SavedLoadStatus size="medium" posting={posting} />
            </Column>
          </Columns>

          <BrokerFeeLine price={load.price} brokerFee={load.broker_fee} />

          <Stack space="xxsmall">
            {carrierPostingInfo != null && (
              <CarrierPostingStatusInfo
                showShipperDeclineReason={true}
                showEditedByShipper={true}
                carrierPostingInfo={carrierPostingInfo}
              />
            )}

            {carrierPostingInfo?.status === 'COUNTER_OFFER_SENT' && (
              <Inline space="xxsmall">
                <Typography color="textSecondary">Your bid was</Typography>
                <Typography variant="body1">
                  {carrierPostingInfo.price}
                </Typography>
              </Inline>
            )}

            <Stack space="xsmall">
              <PostingPaymentDetails
                load={load}
                source="Load Details"
                showDistancePriceDetails={false}
              />

              {load.number && <PostingNumber postingNumber={load.number} />}

              {!isEditedByShipperFlagEnabled &&
                carrierPostingInfo?.load_edited_at && (
                  <PostingEditedLabel
                    loadEditedAt={carrierPostingInfo.load_edited_at}
                    variant="detail"
                  />
                )}

              <PostingPostedDate {...load} />

              <PostingLoadDetailsDates load={load} />
            </Stack>

            {load.payment.method === 'superpay' && (
              <Box marginTop="xxsmall">
                {settings?.superpay.payment_error ? (
                  <BankErrorSimpleBanner
                    source="Load Details"
                    paymentError={settings.superpay.payment_error}
                  />
                ) : (
                  <SuperPayNudgingBanner source="Load Details" />
                )}
              </Box>
            )}
          </Stack>
        </Stack>
      </PostingDetailsCard>

      <PostingDetailsCard header="Origin and Destination">
        <PostingRouteInfo
          load={load}
          variant="long"
          pickupDistance={posting.pickup_distance}
          deliveryDistance={posting.delivery_distance}
        />
      </PostingDetailsCard>

      <PostingVehiclesExpander
        vehicles={load.vehicles}
        render={({ heading, isExpanded }) => (
          <PostingDetailsCard header={heading}>
            <PostingVehicles load={load} isExpanded={isExpanded} />
          </PostingDetailsCard>
        )}
      />

      <PostingDetailsCard
        header={
          <Columns>
            <Column>
              <TextBox variant="heading-4">Shipper</TextBox>
            </Column>

            {load.shipper && inBusinessSince && (
              <Column width="content">
                <TextBox color="secondary">
                  Shipper since {inBusinessSince}
                </TextBox>
              </Column>
            )}
          </Columns>
        }
      >
        {load.shipper && <PostingShipperDetails shipper={load.shipper} />}
      </PostingDetailsCard>

      {!!load.instructions && !!load.shipper && (
        <PostingDetailsCard header="Instructions">
          <Typography aria-label="Instructions">
            <WebViewLinkedText>{load.instructions}</WebViewLinkedText>
          </Typography>
        </PostingDetailsCard>
      )}

      {hasShipperRequirements(load) && (
        <PostingDetailsCard
          header={
            <Inline verticalAlign="center" noWrap={true}>
              {shouldReviewShipperRequirements(load) && (
                <WarningRounded fontSize="small" htmlColor={Color.Yellow300} />
              )}

              <span>Shipper Requirements</span>
            </Inline>
          }
        >
          {load.shipper && (
            <PostingShipperRequirements
              load={load}
              carrierInsuranceRequirements={
                load.shipper.carrier_insurance_requirements?.length ? (
                  <PostingDetailsInsuranceRequirements
                    limits={load.shipper.carrier_insurance_requirements}
                  />
                ) : null
              }
            />
          )}
        </PostingDetailsCard>
      )}

      <PostingDetailsNearbyShipperLoads posting={posting} />

      {postingStatus === 'can_book' && (
        <PostingDetailsCard header="Book Now">
          <SendRequestForm
            posting={posting}
            isBookable={true}
            onScrollToFormClick={onScrollToFormClick}
          />
        </PostingDetailsCard>
      )}

      {postingStatus === 'can_send_request' && (
        <PostingDetailsCard header="Request">
          <SendRequestForm
            posting={posting}
            isBookable={false}
            onScrollToFormClick={onScrollToFormClick}
          />
        </PostingDetailsCard>
      )}

      {postingStatus === 'can_update_request' && !!carrierPostingInfo && (
        <PostingDetailsCard header="Request">
          <UpdateRequestForm
            posting={posting}
            carrierPostingInfo={carrierPostingInfo}
          />
        </PostingDetailsCard>
      )}

      {!!carrierPostingInfo &&
        (postingStatus === 'received_offer' ||
          postingStatus === 'offer_accepted') && (
          <PostingDetailsCard>
            <Stack space="small">
              <CarrierPostingStatusInfo
                carrierPostingInfo={carrierPostingInfo}
              />

              {renderPostingOfferButton(posting)}
            </Stack>
          </PostingDetailsCard>
        )}
    </Stack>
  );
}
